import type { OperatorRunStatus, OperatorRunWire } from '~/hooks/queries/use-operator-runs';
import { openOperatorPanel } from '~/lib/stores/operator-panel-store';
import { operatorTakeoverGate, type OperatorTakeoverGateState } from '~/features/operator/operator-takeover-gate';
import { deriveStopState, stopObservedAt, type StopState } from '~/features/operator/stop-state';

/**
 * FRONT-1989 — decision logic for the `dispatch_desktop_task` chat card
 * (`desktop-task-card.tsx`). Same reasoning as `finish-now-gate.ts` /
 * `smart-tagging-run-gate.ts`: unit-testable without React or a DOM, one
 * definition the card and its test both consult so the two can't drift.
 * Almost all of it is pure; {@link createWatchDesktopHandler} is the one
 * deliberate exception (review R7) — it wraps a store call so the button's
 * actual effect is testable at all in this repo's no-DOM frontend suite.
 *

 * **Why the tool's own output alone isn't enough to drive the card.**
 * `dispatch_desktop_task`'s handler runs the ENTIRE operator loop before its
 * tool call returns, so on the ordinary chat path the AI SDK tool part sits
 * in `input-available` for the whole run's duration — its `output` stays
 * `undefined` until the run is already finished. FRONT-1990 round 2 adds one
 * exception: the unattended workflow door's tool result comes back the
 * instant the run is CREATED, as `{ status: "started", runId, note }` — so
 * `output` there is no longer necessarily final, and `deriveCardPhase` reads
 * `'started'` as in-progress the same way it reads a not-yet-finished
 * `'running'`. Either way, everything the card shows WHILE a run is in
 * progress (steps appearing, the live screenshot, the Stop button) has to
 * come from polling `GET /api/operator/runs/:runId` instead, which is why
 * the card needs its own `runId` before the tool has produced any FINAL
 * output at all — see {@link resolveRunId}.
 */

/**
 * The `dispatch_desktop_task` tool's own result, per the final plan's
 * `runOperatorTask` return shape (`{runId, status, summary, error,
 * errorCode, model, servedModel, stepCount, finalScreenshotUrl, inputTokens,
 * outputTokens, costUsd}` plus `goal`). All fields optional here because a
 * REFUSAL that never reaches the run store (unattended session, no owner
 * machine, autonomy tier 0, missing capability) returns a bare `{error}`
 * with no `runId` at all — that shape routes to the plain `ToolCallCard`
 * instead of this one (message-bubble.tsx's own routing rule), so this type
 * only needs to describe "enough for `resolveRunId`/`deriveCardPhase` to
 * make sense of whatever came back," not a single canonical envelope.
 */
export interface DesktopTaskToolOutput {
  /**
   * The run this call created — or, on the store's "one task at a time"
   * create-time conflict, the run ALREADY in progress that blocked it. Both
   * cases carry a real run id worth showing the person, which is why
   * {@link resolveRunId} doesn't distinguish them.
   */
  runId?: string;
  status?: OperatorRunStatus | 'started';
  goal?: string;
  summary?: string | null;
  error?: string | null;
  /** The loop's own result field. */
  errorCode?: string | null;
  /** The store's create-time conflict path uses `code`, not `errorCode` —
   *  kept as a second, separate optional field rather than reconciled,
   *  since this type describes the wire as the backend actually sends it. */
  code?: string;
  model?: string;
  servedModel?: string | null;
  stepCount?: number;
  finalScreenshotUrl?: string | null;
  inputTokens?: number;
  outputTokens?: number;
  /** Null when the run's model has no price (the backend sends null). */
  costUsd?: number | null;
}

export type DesktopTaskCardPhase =
  | 'awaiting-approval'
  | 'not-started'
  | 'starting'
  | 'running'
  | 'needs-help'
  | 'in-control'
  /** FRONT-2341: `running`, a stop has been requested, and no takeover is
   *  in progress — see `deriveCardPhase` point 2 below and `stop-state.ts`
   *  for the full precedence. */
  | 'stopping'
  | 'succeeded'
  | 'failed'
  | 'stopped'
  | 'unavailable';

/**
 * The tool's own runId, when the tool has actually finished, WINS over the
 * toolCallId lookup's runId — the lookup exists only to bridge the gap
 * before the tool has produced output (a run still in progress, or a page
 * reload that landed mid-run before this tab ever saw the tool result).
 * Once real output exists it is authoritative; a stale lookup entry must
 * never override it.
 */
export function resolveRunId(input: {
  output: DesktopTaskToolOutput | undefined;
  lookupRunId: string | undefined;
}): string | undefined {
  return input.output?.runId ?? input.lookupRunId;
}

/**
 * The card's display phase. Precedence, most-authoritative first:
 *
 * 1. `runError` — a failed fetch of the run detail. Checked before `run`
 *    because TanStack Query can keep a previous successful `run` around
 *    while a later refetch is erroring; showing that stale snapshot as
 *    current would be worse than admitting the card can't tell right now.
 * 2. `run` — the live, polled/SSE-invalidated detail. Whenever it's
 *    present it reflects the backend's own status directly — EXCEPT while
 *    `running` with a `takeover` in progress (FRONT-1996 "Take the
 *    wheel"), which reads as `'needs-help'` (Astra asked, `requested`) or
 *    `'in-control'` (a person actually has the keyboard, `active`) instead
 *    of the plain `'running'` a viewer would otherwise have no way to tell
 *    apart from ordinary progress. A finished run never carries a
 *    `takeover` at all (the backend clears it in `finishRun`), so this
 *    branch is unreachable for any status but `running`. Checked next,
 *    still only for `running`: a pending stop (FRONT-2341,
 *    `run.stopRequestedAt` set) with no takeover in progress reads as
 *    `'stopping'` rather than plain `'running'` — takeover is checked
 *    first because a stop requested mid-takeover is the rarer case and the
 *    person's attention belongs on the takeover banner, not the badge.
 * 3. `output` — the tool's own result (final on the ordinary chat path, but
 *    NOT on the unattended door's — see the module docblock), used only
 *    until `run` has loaded for the first time. `output.status === 'running'`
 *    OR `'started'` both read as `'starting'`: the store row exists (the
 *    loop is under way) but this tab hasn't fetched its detail yet.
 * 4. `toolState` — no run and no output yet at all: the tool call is still
 *    being proposed/approved (`input-streaming`/`input-available`) or is in
 *    some state this card has no evidence for (`not-started`).
 */
export function deriveCardPhase(input: {
  toolState: string | undefined;
  output: DesktopTaskToolOutput | undefined;
  run: OperatorRunWire | undefined;
  runError: unknown;
}): DesktopTaskCardPhase {
  const { toolState, output, run, runError } = input;

  if (runError) return 'unavailable';
  if (run) {
    if (run.status === 'running' && run.takeover) {
      return run.takeover.state === 'active' ? 'in-control' : 'needs-help';
    }
    if (run.status === 'running' && run.stopRequestedAt) return 'stopping';
    return run.status;
  }
  if (output?.status) {
    // 'started' (FRONT-1990 round 2, the unattended tool result) and
    // 'running' both mean "the run exists and is under way, this tab just
    // hasn't fetched its detail yet" — same in-progress reading either way.
    return output.status === 'running' || output.status === 'started' ? 'starting' : output.status;
  }
  if (toolState === 'input-streaming' || toolState === 'input-available') return 'awaiting-approval';
  return 'not-started';
}

/**
 * The large "final screenshot" shown once a run is finished — a precedence
 * rule with the same shape as {@link resolveRunId}: the run's OWN last step
 * carrying a screenshot wins (it's the freshest, most-detailed source, and
 * is present whenever the run detail has fully loaded), falling back to the
 * tool output's `finalScreenshotUrl` only when the run side has nothing
 * usable — e.g. the tool call already returned its final output but this
 * tab hasn't finished loading the run detail (or the run's steps carry no
 * screenshot at all). Every case below is exercised independently per the
 * "precedence rule tested from one side is not tested" rule: run alone,
 * output alone, both agreeing, and run present but UNUSABLE (no step
 * screenshot) so the output fallback must be used.
 */
export function resolveFinalScreenshotUrl(input: {
  run: OperatorRunWire | undefined;
  output: DesktopTaskToolOutput | undefined;
}): string | undefined {
  const steps = input.run?.steps ?? [];
  let fromRun: string | null | undefined;
  for (let i = steps.length - 1; i >= 0; i--) {
    if (steps[i].screenshotUrl) {
      fromRun = steps[i].screenshotUrl;
      break;
    }
  }
  return fromRun ?? input.output?.finalScreenshotUrl ?? undefined;
}

export type StopControlState = StopState;

/**
 * The card's Stop button — a thin wrapper over the shared
 * `deriveStopState` (`~/features/operator/stop-state.ts`), which the full
 * `OperatorPanel` also consults, so the two surfaces can't disagree about
 * what a given run's stop state looks like. `disabled` is derived FROM the
 * phase (FRONT-1670's "a disabled control and its explanation are one
 * decision" rule) inside that shared function.
 *
 * The button only ever shows for a run that is actually `running` — a
 * finished run has nothing left to stop, and the plan's own idempotent-stop
 * contract ("finished run → 200 unchanged") means clicking it then would do
 * nothing anyway.
 *
 * The slow-stop clock starts at this browser's first sighting of the stop
 * (`stopObservedAt`, shared with the panel) once there is one, falling back
 * to the server's `stopRequestedAt` only before that first sighting exists
 * — see `stopClockStart`'s own doc for why a skewed laptop clock (running
 * either ahead of OR behind the server's) can't distort the 30s window.
 * `observedAt` is returned so the card's boundary timer runs on that same
 * clock.
 */
export function stopControl(
  run: OperatorRunWire | undefined,
  pending: boolean,
  now: number = Date.now(),
): StopControlState & { observedAt: number | null } {
  if (!run) return { phase: 'hidden', show: false, label: 'Stop', disabled: false, note: null, observedAt: null };
  const observedAt = stopObservedAt(run.id, run.stopRequestedAt, now);
  return {
    ...deriveStopState({ status: run.status, stopRequestedAt: run.stopRequestedAt, pending, now, observedAt }),
    observedAt,
  };
}

export interface WatchDesktopControlState {
  show: boolean;
  runId?: string;
}

/**
 * FRONT-1989 review R7: the "Watch desktop" button's own show/target
 * decision, pulled out of the component so a regression can't hide behind a
 * component test that never inspects what the button actually opens. Two
 * things had drifted apart before this: the button rendered whenever a
 * runId existed at all — including the `unavailable` phase (a 404 or the
 * feature flag off), where the panel has nothing to load — and its handler
 * could silently degrade to `openOperatorPanel({})` (the bare "just watch
 * the desktop" request) without any test noticing, since a component test
 * asserting presence of the button element passes either way.
 *
 * `show` is true only once a runId is resolvable AND the phase isn't
 * `unavailable`; `runId` is carried alongside so the caller never has to
 * re-derive it (and can't accidentally pass a stale one from closure).
 */
export function watchDesktopControl(input: {
  runId: string | undefined;
  phase: DesktopTaskCardPhase;
}): WatchDesktopControlState {
  if (!input.runId || input.phase === 'unavailable') return { show: false };
  return { show: true, runId: input.runId };
}

/**
 * The button's click handler, built from {@link watchDesktopControl}'s own
 * decision — kept here, not in the component, so `desktop-task-card-model.test.ts`
 * can assert the EXACT request raised (`{ runId: '<id>' }`, never a bare
 * `{}`) with `~/lib/stores/operator-panel-store` mocked, rather than only a
 * component test asserting the button rendered. This repo's frontend suite
 * has no DOM (`renderToStaticMarkup` only), so `desktop-task-card.test.tsx`
 * cannot click the button itself — this factory is what makes the click's
 * actual effect testable at all.
 */
export function createWatchDesktopHandler(control: WatchDesktopControlState): () => void {
  return () => {
    if (control.show && control.runId) openOperatorPanel({ runId: control.runId });
  };
}

/**
 * FRONT-1996 "Take the wheel": the card's own Take over/Hand back controls
 * reuse the exact same pure gate as `operator-panel.tsx`
 * (`operatorTakeoverGate`) so the two surfaces can never disagree about
 * when a button is available or why not — see that module's own docblock
 * for the full "disabled iff it has a reason" contract, swept in
 * `operator-takeover-gate.test.ts`.
 */
export function takeoverControl(input: {
  run: OperatorRunWire | undefined;
  takingOver: boolean;
  handingBack: boolean;
  /** FRONT-1996 review M11: a `POST .../stop` for this run is in flight —
   *  see `operatorTakeoverGate`'s own doc on why this disables both
   *  buttons with "Stopping…" rather than leaving them clickable. */
  stopping: boolean;
}): OperatorTakeoverGateState {
  return operatorTakeoverGate({
    status: input.run?.status,
    takeover: input.run?.takeover,
    takingOver: input.takingOver,
    handingBack: input.handingBack,
    stopping: input.stopping,
  });
}

/**
 * "Take over" from the card does two things at once — see the module
 * docblock's contract ("opens the panel and takes over"): the person needs
 * the panel open to actually reach the desktop, and clicking Take over is
 * itself the action that starts the takeover, not merely a shortcut to a
 * button inside the panel. Built as a factory for the same reason
 * {@link createWatchDesktopHandler} is: this repo's frontend suite has no
 * DOM, so a component test can't click the button and observe the effect
 * any other way.
 */
export function createTakeOverHandler(input: {
  runId: string | undefined;
  takeOver: (input: { runId: string }) => void;
}): () => void {
  return () => {
    if (!input.runId) return;
    openOperatorPanel({ runId: input.runId });
    input.takeOver({ runId: input.runId });
  };
}

/**
 * "Hand back" from the card is just the mutation — unlike Take over there
 * is nothing new to open: a Hand back button only ever shows once a
 * takeover is already ACTIVE, which is only reachable after the panel was
 * opened at least once.
 */
export function createHandBackHandler(input: {
  runId: string | undefined;
  handBack: (input: { runId: string }) => void;
}): () => void {
  return () => {
    if (!input.runId) return;
    input.handBack({ runId: input.runId });
  };
}
